import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleXmark,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import type { HtmlIssue } from '@/lib/email-html';

interface IssueListProps {
  title: string;
  issues: HtmlIssue[];
  emptyMessage: string;
}

/**
 * Renders HTML validation / email-safety findings.
 *
 * Errors and warnings are visually distinct because they mean different things:
 * an error blocks the action, a warning is the author's judgement call.
 */
export function IssueList({ title, issues, emptyMessage }: IssueListProps) {
  const errors = issues.filter((i) => i.severity === 'error');
  const warnings = issues.filter((i) => i.severity === 'warning');

  return (
    <div className="rounded-md border border-border">
      <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/30 px-3 py-2">
        <span className="text-xs font-semibold text-foreground">{title}</span>
        <span className="flex items-center gap-2 text-[11px]">
          {errors.length > 0 && (
            <span className="text-red-600 dark:text-red-400">
              {errors.length} error{errors.length === 1 ? '' : 's'}
            </span>
          )}
          {warnings.length > 0 && (
            <span className="text-amber-600 dark:text-amber-500">
              {warnings.length} warning{warnings.length === 1 ? '' : 's'}
            </span>
          )}
          {issues.length === 0 && (
            <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
              <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3" />
              Passed
            </span>
          )}
        </span>
      </div>

      {issues.length === 0 ? (
        <p className="px-3 py-2 text-xs text-muted-foreground">{emptyMessage}</p>
      ) : (
        <ul className="max-h-52 divide-y divide-border overflow-auto">
          {[...errors, ...warnings].map((issue, i) => (
            <li key={`${issue.code}-${issue.line ?? i}-${i}`} className="flex gap-2 px-3 py-2">
              <FontAwesomeIcon
                icon={issue.severity === 'error' ? faCircleXmark : faTriangleExclamation}
                className={
                  issue.severity === 'error'
                    ? 'mt-0.5 h-3 w-3 shrink-0 text-red-600 dark:text-red-400'
                    : 'mt-0.5 h-3 w-3 shrink-0 text-amber-600 dark:text-amber-500'
                }
              />
              <div className="min-w-0 text-xs">
                <p className="text-foreground">
                  {issue.message}
                  {issue.line !== undefined && (
                    <span className="ml-1.5 font-mono text-[10px] text-muted-foreground">
                      line {issue.line}
                    </span>
                  )}
                </p>
                {issue.detail && <p className="mt-0.5 text-muted-foreground">{issue.detail}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
